import { getApiUrl, getAuthHeaders } from '../config/api';
import { jwtDecode } from 'jwt-decode';
import { apiRequest } from './apiClient';

class AuthService {
  // Admin login
  static async adminLogin(credentials) {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/auth/admin/login',
        method: 'POST',
        auth: false,
        body: credentials,
        showSuccess: true,
        successMessage: 'Login successful'
      });

      if (result.success) {
        // Store tokens
        localStorage.setItem('access_token', result.data.access_token);
        localStorage.setItem('refresh_token', result.data.refresh_token);

        // Return basic user data since API doesn't include user info in response
        const userData = {
          email: credentials.email,
          username: credentials.email.split('@')[0],
          name: 'Admin User',
          phone: '',
          avatar: '',
          address: '',
          role: 'admin',
          status: true
        };

        return {
          success: true,
          data: userData
        };
      } else {
        return {
          success: false,
          message: result.message || 'Login failed'
        };
      }
    } catch (error) {
      console.error('Admin login error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Refresh token
  static async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(getApiUrl('/admin/api/auth/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      const result = await response.json();

      if (result.status) {
        localStorage.setItem('access_token', result.data.access_token);
        return result.data.access_token;
      }

      throw new Error('Token refresh failed');
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      throw error;
    }
  }

  // Logout
  static async logout() {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Check if user is authenticated
  static isAuthenticated() {
    const token = localStorage.getItem('access_token');
    if (!token) return false;

    try {
      // Check if token is expired
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  // Get current user data from token
  static getCurrentUser() {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    try {
      // Check if token is expired first
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;

      if (payload.exp <= currentTime) {
        console.log('Token expired, removing from storage');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return null;
      }

      return {
        email: payload.email || 'admin@example.com',
        username: payload.username || payload.email?.split('@')[0] || 'admin',
        name: payload.name || 'Admin User',
        phone: payload.phone || '',
        avatar: payload.avatar || '',
        address: payload.address || '',
        role: payload.user_role || payload.role || 'admin',
        status: payload.status !== undefined ? payload.status : true
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      // Clear invalid tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return null;
    }
  }
}

export default AuthService;
